import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import TimeNotePopover from './TimeNotePopover';
import { formatCell, isOnStep, toEntryUnit, unitLabel } from './timeUnits';

import ChatBubbleIcon from 'Assets/icons/chat-bubble.svg';

/**
 * The input a cell turns into while it is being edited. Mounted fresh on each
 * opening so it starts from the cell's current value, including when Tab opens
 * it from the neighbouring day.
 */
function CellEditor({ initial, step, onCommit, onTab, onCancel, onDone }) {
    const [draft, setDraft] = useState(initial);
    const inputRef = useRef(null);
    // Set once a keystroke has already committed or cancelled, so the blur
    // that follows does not commit a second time.
    const settledRef = useRef(false);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            settledRef.current = true;
            onCommit(draft);
            onDone();
        } else if (event.key === 'Escape') {
            settledRef.current = true;
            onCancel();
            onDone();
        } else if (event.key === 'Tab') {
            settledRef.current = true;

            // A refused value, or no editable day left in that direction,
            // leaves the key alone: focus moves on as it normally would and
            // the blur closes the editor.
            if (onCommit(draft) && onTab(event.shiftKey ? -1 : 1)) {
                event.preventDefault();
            }
        }
    };

    const handleBlur = () => {
        if (!settledRef.current) {
            onCommit(draft);
        }

        onDone();
    };

    return (
        <input
            ref={inputRef}
            type="number"
            step={step}
            min="0"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            aria-label={$t('Time recorded')}
            className="tw-w-full tw-text-end tw-bg-white tw-border tw-border-blue tw-rounded-lg tw-px-1 tw-py-0 tw-text-sm tw-text-charcoal tw-outline-none"
        />
    );
}

/**
 * One day of one row. Click to edit, Enter commits, Escape cancels, and leaving
 * the box commits too — the same behaviour as the overridable cells elsewhere in
 * the project, so the grid feels like the rest of the app. Tab commits and
 * moves the editor along the row; the row decides which day that is.
 *
 * A cell saves only when the value actually changed. A locked day (on or before
 * the project's lock date) and a day no rate card covers are both read-only, as
 * is every day of a submitted week, and each says which when hovered rather
 * than silently refusing a click.
 */
export default function TimeDayCell({
    quantity,
    note,
    locked,
    covered,
    submitted = false,
    weekend,
    options,
    noteContext,
    editing,
    onStartEdit,
    onStopEdit,
    onTab,
    onSave,
    onNoteSave,
}) {
    const [noteOpen, setNoteOpen] = useState(false);
    const [error, setError] = useState(null);
    const noteButtonRef = useRef(null);

    const readOnly = submitted || locked || !covered;
    const displayed = toEntryUnit(quantity, options);

    const startEditing = () => {
        if (readOnly) {
            return;
        }

        setError(null);
        onStartEdit();
    };

    /**
     * Validates and saves a typed value. Returns whether it was accepted, so
     * Tab can stop on a day whose value was refused rather than moving past
     * the error.
     */
    const commit = (draft) => {
        const trimmed = draft.trim();
        const parsed = trimmed === '' ? 0 : Number(trimmed);

        if (!Number.isFinite(parsed) || parsed < 0) {
            setError($t('Enter a number of {{unit}}, or nothing to clear the day', { unit: unitLabel(options) }));

            return false;
        }

        if (!isOnStep(parsed, options)) {
            setError($t('Time is recorded in steps of {{step}} {{unit}}', { step: options?.step, unit: unitLabel(options) }));

            return false;
        }

        setError(null);

        if (parsed !== displayed) {
            onSave(parsed);
        }

        return true;
    };

    // Recording time is the primary interaction of this page, so the cell has to
    // be reachable without a mouse. Enter and Space open the editor, matching
    // the role the cell advertises. Only when the cell itself has focus — a
    // key pressed on the note button inside it belongs to that button.
    const handleCellKeyDown = (event) => {
        if (readOnly || event.target !== event.currentTarget) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            startEditing();
        }
    };

    const closeNote = (restoreFocus) => {
        setNoteOpen(false);

        if (restoreFocus) {
            noteButtonRef.current?.focus();
        }
    };

    const reason = submitted
        ? $t('This week has been submitted')
        : locked
          ? $t('This day is locked and can no longer be changed')
          : !covered
            ? $t('No rate card is in force on this day')
            : null;

    // The reason and the note travel in the accessible name as well as the
    // tooltip — a title attribute alone is invisible to a screen reader and to
    // anybody navigating by keyboard.
    const label = [`${$t('Time recorded')}: ${formatCell(quantity, options) || 0}`, reason, note ? `${$t('Note')}: ${note}` : null].filter(Boolean).join(' — ');

    // Important because the shared table styles make every body cell inherit
    // the row's background with a more specific selector.
    const weekendClass = { '!tw-bg-charcoal-15/50': weekend && !error };

    if (editing) {
        return (
            <td className={classNames('inbud-numeric-cell tw-p-0', weekendClass)}>
                <CellEditor
                    initial={displayed === 0 ? '' : String(displayed)}
                    step={options?.step}
                    onCommit={commit}
                    onTab={onTab}
                    onCancel={() => setError(null)}
                    onDone={onStopEdit}
                />
            </td>
        );
    }

    // A note is editable only where there is something to attach it to: a zero
    // quantity has no entry, and saving one would delete it. A note already on
    // a read-only day stays visible, just not editable.
    const canEditNote = !readOnly && !!quantity;
    const noteColour = note ? 'tw-text-blue' : 'tw-text-charcoal-50';
    const noteDot = !!note && <span aria-hidden="true" className="tw-absolute -tw-top-0.5 -tw-end-0.5 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue" />;

    return (
        <td
            className={classNames('inbud-numeric-cell tw-relative tw-select-none', weekendClass, {
                'tw-cursor-pointer': !readOnly,
                'tw-cursor-not-allowed tw-bg-charcoal-10 tw-text-charcoal-50': readOnly,
                '!tw-bg-red-50': !!error,
            })}
            title={reason ?? undefined}
            aria-label={label}
            aria-disabled={readOnly || undefined}
            role={readOnly ? undefined : 'button'}
            tabIndex={readOnly ? undefined : 0}
            onKeyDown={handleCellKeyDown}
            onClick={startEditing}
        >
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                <span>{formatCell(quantity, options) || '—'}</span>
                {canEditNote && (
                    <button
                        ref={noteButtonRef}
                        type="button"
                        aria-label={$t('Add or edit the note on this day')}
                        aria-expanded={noteOpen}
                        className={classNames('tw-relative tw-inline-flex tw-border-0 tw-bg-transparent tw-p-0 hover:tw-text-blue', noteColour)}
                        onClick={(event) => {
                            event.stopPropagation();
                            setNoteOpen((open) => !open);
                        }}
                    >
                        <ChatBubbleIcon className="tw-h-3 tw-w-3" />
                        {noteDot}
                    </button>
                )}
                {!canEditNote && !!note && (
                    <span className={classNames('tw-relative tw-inline-flex', noteColour)} title={note}>
                        <ChatBubbleIcon className="tw-h-3 tw-w-3" />
                        {noteDot}
                    </span>
                )}
            </span>

            {!!error && <div className="tw-text-red-500 tw-text-xs tw-text-end">{error}</div>}

            {noteOpen && <TimeNotePopover anchorRef={noteButtonRef} note={note} context={noteContext} onSave={onNoteSave} onClose={closeNote} />}
        </td>
    );
}
